/** Entry mutations.
 *
 *  Every transition is revision-guarded: `POST /v1/entries/{id}/transitions`
 *  requires both an `Idempotency-Key` and an `If-Match`, so the ETag has to be
 *  read immediately before the write. The entry list does not carry ETags, so
 *  each mutation re-reads its entry first — one extra request that buys a real
 *  conflict signal instead of a silent overwrite.
 */

import { ApiError, request, type Session } from './client';
import { newIdempotencyKey } from './ids';
import type { EntryKind, EntryState, WireEntry } from './wire';

export type TransitionAction =
  | 'complete'
  | 'reopen'
  | 'cancel'
  | 'uncancel'
  | 'migrate'
  | 'schedule'
  | 'migrate_month'
  | 'answer'
  | 'reopen_question';

/** The transition that puts an entry back, for the toast's UNDO.
 *
 *  Only the reversible pairs. `migrate` and `schedule` move an entry and leave a
 *  `>` behind, so undoing them is not a single transition; `answer` would need
 *  the prior answer text. Those return null and their toast carries no UNDO,
 *  which is the honest outcome — the sheet only claims undo where it exists. */
export function inverseOf(action: TransitionAction): TransitionAction | null {
  switch (action) {
    case 'complete':
      return 'reopen';
    case 'reopen':
      return 'complete';
    case 'cancel':
      return 'uncancel';
    case 'uncancel':
      return 'cancel';
    default:
      return null;
  }
}

export interface Conflict {
  kind: 'conflict';
  /** The revision this edit was made against. */
  mine: number;
  /** Where the server actually is now. */
  server: WireEntry;
}

export type MutationResult = { kind: 'ok' } | Conflict;

async function currentEntry(session: Session, id: string) {
  return request<WireEntry>(session, `/v1/entries/${id}`);
}

/** Run a transition against the entry's current revision.
 *
 *  A 409 is not an error to swallow: it means someone else — the TUI, the CLI,
 *  another device — changed this entry. It comes back as a value so the caller
 *  can show both versions and let a person decide, which is what the design
 *  requires and what auto-merging would take away.
 */
export async function transition(
  session: Session,
  id: string,
  action: TransitionAction,
  extra: Record<string, string> = {},
): Promise<MutationResult> {
  const { body: before, etag } = await currentEntry(session, id);
  try {
    await request(session, `/v1/entries/${id}/transitions`, {
      method: 'POST',
      etag,
      idempotencyKey: newIdempotencyKey(),
      body: { action, ...extra },
    });
    return { kind: 'ok' };
  } catch (error) {
    if (error instanceof ApiError && error.isRevisionConflict) {
      const { body: server } = await currentEntry(session, id);
      return { kind: 'conflict', mine: before.revision, server };
    }
    throw error;
  }
}

export async function editEntry(
  session: Session,
  id: string,
  changes: { content?: string; priority?: boolean },
): Promise<MutationResult> {
  const { body: before, etag } = await currentEntry(session, id);
  try {
    await request(session, `/v1/entries/${id}`, {
      method: 'PATCH',
      etag,
      idempotencyKey: newIdempotencyKey(),
      body: changes,
    });
    return { kind: 'ok' };
  } catch (error) {
    if (error instanceof ApiError && error.isRevisionConflict) {
      const { body: server } = await currentEntry(session, id);
      return { kind: 'conflict', mine: before.revision, server };
    }
    throw error;
  }
}

/** Tomorrow, in journal terms: the day after the date shown, not the device's
 *  idea of tomorrow. Dates are inclusive and timezone-resolved server-side, so
 *  this only ever shifts a date the server already gave us. */
export function nextDay(journalDate: string): string {
  const at = new Date(`${journalDate}T00:00:00Z`);
  at.setUTCDate(at.getUTCDate() + 1);
  return at.toISOString().slice(0, 10);
}

/** Which actions apply to an entry, given its kind and state.
 *
 *  Offering an action the server will refuse is worse than not offering it: the
 *  refusal arrives as a 422 after a tap that looked like it should work.
 */
export interface Choice {
  action: TransitionAction | 'edit';
  label: string;
  /** Stated before the tap, per the design's sheet rows. */
  consequence: string;
  /** Destructive enough to need a second, worded confirmation. */
  confirm?: boolean;
  /** Needs input, so it opens a further step rather than firing. */
  prompts?: 'date' | 'month' | 'text';
  /** Fires immediately against a date the caller computes, rather than asking.
   *  `Migrate to tomorrow` is the common case and should not cost a date picker. */
  immediate?: 'tomorrow';
}

/** What deciding an entry's actions actually requires.
 *
 *  Narrower than `WireEntry` on purpose. `WireEntry` carries the service's
 *  date-xor-month invariant as a `oneOf`, and this function's whole job is to
 *  reason about placement — including combinations the service would never emit,
 *  which is exactly what the placement tests construct. A real entry satisfies
 *  this structurally, so nothing at the call sites changes. */
export interface EntryChoiceInput {
  kind: EntryKind;
  state: EntryState;
  active_date: string | null;
  active_month: string | null;
}

export function choicesFor(entry: EntryChoiceInput): Choice[] {
  const out: Choice[] = [];

  if (entry.kind === 'task') {
    if (entry.state === 'open') {
      // `one tap · undoable` is the design's wording, and it is only true
      // because the toast now wires an inverse transition.
      out.push({ action: 'complete', label: 'Complete', consequence: 'one tap · undoable' });
    } else if (entry.state === 'done') {
      out.push({ action: 'reopen', label: 'Reopen', consequence: 'back to open' });
    }
  }
  if (entry.kind === 'question' && entry.state === 'answered') {
    out.push({ action: 'reopen_question', label: 'Reopen', consequence: 'keeps the answer' });
  }
  // An open question can be answered. The server has had this transition all
  // along — `answer` with an `answer_text` — and the client never offered it, so
  // a question captured on the phone could not be answered on the phone.
  if (entry.kind === 'question' && entry.state === 'open') {
    out.push({
      action: 'answer',
      label: 'Answer it',
      // Short, like the other consequences: a long one squeezes the label
      // column and wrapped `Answer it` onto two lines.
      consequence: 'records the answer',
      prompts: 'text',
    });
  }

  if (entry.state === 'open') {
    out.push({ action: 'edit', label: 'Edit text', consequence: 'keeps its history' });

    // Placement decides which moves exist, and the server is strict about it:
    // `Migrate` requires `Placement::Date`, while `Schedule` and `MigrateMonth`
    // both require `Placement::Month`. Offering the wrong one turns a confident
    // tap into `invalid_transition`, which is how this was found — "Migrate to a
    // month" was offered on a dated task and refused.
    const dated = Boolean(entry.active_date) && !entry.active_month;
    const monthScoped = Boolean(entry.active_month) && !entry.active_date;

    if (entry.kind === 'task' && dated) {
      // Tomorrow first and without a picker: it is the migration people
      // actually make, and the reference sheet gives it its own row.
      out.push({
        action: 'migrate',
        label: 'Migrate to tomorrow',
        consequence: 'source stays visible, marked >',
        immediate: 'tomorrow',
      });
      out.push({
        action: 'migrate',
        label: 'Migrate to a date',
        consequence: 'source stays visible, marked >',
        prompts: 'date',
      });
    }

    if (entry.kind === 'task' && monthScoped) {
      out.push({
        action: 'schedule',
        label: 'Schedule to a date',
        consequence: 'leaves the month inbox',
        prompts: 'date',
      });
      out.push({
        action: 'migrate_month',
        label: 'Migrate to a month',
        consequence: 'moves to a later month inbox',
        prompts: 'month',
      });
    }
  }

  if (entry.state === 'cancelled') {
    out.push({ action: 'uncancel', label: 'Uncancel', consequence: 'back to open' });
  } else if (entry.state !== 'migrated') {
    out.push({
      action: 'cancel',
      label: 'Cancel',
      consequence: 'stays visible, struck through',
      confirm: true,
    });
  }

  return out;
}
